<template>
	<div class="topic-text-editor" :class="{ 'topic-text-editor--disabled': disabled }">
		<div ref="toolbarContainer" class="topic-text-editor__toolbar"></div>
		<div
			class="topic-text-editor__surface"
			:class="[editorClass, { 'topic-text-editor__surface--error': error }]"
		>
			<div ref="editorHost" class="topic-text-editor__host"></div>
		</div>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { DecoupledEditor, editorPlugins, editorTranslations } from './ckeditor-editor'

const READ_ONLY_LOCK = 'topic-text-editor-disabled'

const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: '',
	},
	minHeight: {
		type: [Number, String],
		default: 120,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	error: {
		type: Boolean,
		default: false,
	},
	editorClass: {
		type: [String, Array, Object],
		default: '',
	},
})

const emit = defineEmits(['update:modelValue', 'blur'])

const toolbarContainer = ref(null)
const editorHost = ref(null)
let editorInstance = null
let isApplyingExternalModelValue = false
let isUnmounted = false

const resolvedMinHeight = computed(() => {
	const raw = props.minHeight
	if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
		return `${raw}px`
	}
	return String(raw || '120px')
})

const setReadOnlyState = value => {
	if (!editorInstance) return
	try {
		if (value) {
			editorInstance.enableReadOnlyMode(READ_ONLY_LOCK)
		} else {
			editorInstance.disableReadOnlyMode(READ_ONLY_LOCK)
		}
	} catch (error) {
		editorInstance.isReadOnly = !!value
	}
}

const applyMinHeight = () => {
	if (!editorHost.value) return
	const editable = editorHost.value.querySelector('.ck-editor__editable')
	if (!editable) return
	editable.style.minHeight = resolvedMinHeight.value
}

const createEditor = async () => {
	if (!editorHost.value || !toolbarContainer.value) return
	const hostEl = editorHost.value
	const toolbarEl = toolbarContainer.value

	const editor = await DecoupledEditor.create(hostEl, {
		licenseKey: 'GPL',
		plugins: editorPlugins,
		translations: editorTranslations,
		language: 'ru',
		placeholder: props.placeholder,
		toolbar: {
			items: [
				'undo',
				'redo',
				'|',
				'bold',
				'italic',
				'underline',
				'|',
				'bulletedList',
				'numberedList',
				'|',
				'link',
				'|',
				'removeFormat',
			],
			shouldNotGroupWhenFull: true,
		},
		link: {
			defaultProtocol: 'https://',
			addTargetToExternalLinks: true,
		},
		image: {
			toolbar: [
				'imageStyle:inline',
				'imageStyle:block',
				'|',
				'toggleImageCaption',
				'imageTextAlternative',
				'|',
				'linkImage'
			]
		},
		table: {
			contentToolbar: [
				'tableColumn', 'tableRow', 'mergeTableCells', '|',
				'tableProperties', 'tableCellProperties'
			]
		},
		mediaEmbed: {
			toolbar: ['mediaEmbed']
		},
	})

	if (isUnmounted) {
		await editor.destroy()
		return
	}

	editorInstance = editor
	const toolbarViewEl = editor.ui?.view?.toolbar?.element
	if (!toolbarEl || !toolbarViewEl) {
		await editor.destroy()
		editorInstance = null
		return
	}
	toolbarEl.appendChild(toolbarViewEl)

	const initialValue = props.modelValue || ''
	if (initialValue && initialValue !== editor.getData()) {
		isApplyingExternalModelValue = true
		await editor.setData(initialValue)
		isApplyingExternalModelValue = false
	}

	setReadOnlyState(props.disabled)
	applyMinHeight()

	editor.model.document.on('change:data', () => {
		if (isApplyingExternalModelValue) return
		emit('update:modelValue', editor.getData())
	})

	editor.editing.view.document.on('blur', () => {
		emit('blur')
	})
}

watch(
	() => props.modelValue,
	async nextValue => {
		if (!editorInstance) return
		const incoming = nextValue || ''
		const current = editorInstance.getData()
		if (incoming === current) return

		isApplyingExternalModelValue = true
		try {
			await editorInstance.setData(incoming)
		} finally {
			isApplyingExternalModelValue = false
			applyMinHeight()
		}
	}
)

watch(
	() => props.disabled,
	nextDisabled => {
		setReadOnlyState(nextDisabled)
	}
)

watch(
	() => resolvedMinHeight.value,
	() => {
		applyMinHeight()
	}
)

onMounted(async () => {
	isUnmounted = false
	try {
		await nextTick()
		await createEditor()
	} catch (error) {
		console.error('[TopicTextEditor] Failed to init editor:', error)
	}
})

onBeforeUnmount(async () => {
	isUnmounted = true
	if (!editorInstance) return
	try {
		await editorInstance.destroy()
	} catch (error) {
		console.warn('[TopicTextEditor] Failed to destroy editor:', error)
	}
	editorInstance = null
})
</script>

<style scoped>
.topic-text-editor {
	display: flex;
	flex-direction: column;
	gap: 0;
	width: 100%;
}

.topic-text-editor__toolbar :deep(.ck-toolbar) {
	border: 1px solid #d1d5db;
	border-radius: 8px 8px 0 0;
	background: #f8fafc;
	padding: 6px;
}

.topic-text-editor__surface :deep(.ck.ck-editor__editable) {
	border: 1px solid #d1d5db;
	border-top: none;
	border-radius: 0 0 8px 8px;
	padding: 12px;
	font-size: 14px;
	line-height: 1.6;
	box-shadow: none;
}

.topic-text-editor__surface :deep(.ck.ck-editor__editable.ck-focused) {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.topic-text-editor__surface--error :deep(.ck.ck-editor__editable),
.topic-text-editor__surface--error :deep(.ck-toolbar) {
	border-color: #e11d48 !important;
}

.topic-text-editor--disabled {
	opacity: 0.78;
}

.topic-text-editor__surface :deep(.ck-content ul),
.topic-text-editor__surface :deep(.ck-content ol) {
	padding-left: 1.4em;
}

.topic-text-editor__surface :deep(.ck-content p) {
	margin: 0 0 10px 0;
}

.topic-text-editor__surface :deep(.ck-content p:last-child) {
	margin-bottom: 0;
}

.topic-text-editor :deep(.ck-powered-by),
.topic-text-editor :deep([class*='powered-by']) {
	display: none !important;
	visibility: hidden !important;
}
</style>

<style>
.ck-powered-by,
.ck.ck-powered-by,
[class*='ck-powered-by'],
[class*='powered-by'][class*='ck'] {
	display: none !important;
	visibility: hidden !important;
	opacity: 0 !important;
	pointer-events: none !important;
}
</style>
